/**
 * CortexAdventure — Interactive terminal demo for Cortex.
 *
 * Choose-your-own-adventure through 4 paths of Cortex capabilities:
 * Memory Layer, Cost Engine, Portfolio Brain, The Future.
 *
 * Full-screen terminal aesthetic with boot sequence, typewriter effects,
 * hub routing visualizations, and keyboard navigation.
 *
 * Route: /cortex/demo
 */
import { useState, useEffect, useRef, useCallback, type ReactElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ALL_PATHS,
  SCENE_MAP,
  type AdventureLine,
  type AdventureScene,
} from '../../data/cortex-adventure';
import { HubVisualization } from './HubVisualization';

// ── Boot Sequence ────────────────────────────────────────

interface BootLine {
  text: string;
  style: 'command' | 'system' | 'status' | 'blank' | 'ready';
  delayMs: number;
}

const BOOT_LINES: BootLine[] = [
  { text: '$ cortex --demo', style: 'command', delayMs: 0 },
  { text: '', style: 'blank', delayMs: 300 },
  { text: 'cortex v3.0.0 -- intelligence exoskeleton', style: 'system', delayMs: 500 },
  { text: 'loading intelligence layer...', style: 'system', delayMs: 800 },
  { text: '', style: 'blank', delayMs: 1000 },
  { text: '  memory layer     ... 2,847 entries', style: 'status', delayMs: 1200 },
  { text: '  conductor        ... 6 providers, 12 models', style: 'status', delayMs: 1500 },
  { text: '  learning loop    ... 853 outcomes tracked', style: 'status', delayMs: 1800 },
  { text: '  agent network    ... 12 agents operational', style: 'status', delayMs: 2100 },
  { text: '  portfolio        ... 5,700+ tests across 6 projects', style: 'status', delayMs: 2400 },
  { text: '', style: 'blank', delayMs: 2700 },
  { text: 'ready', style: 'ready', delayMs: 3000 },
];

const BOOT_COLORS: Record<BootLine['style'], string> = {
  command: 'text-amber-400',
  system: 'text-gray-500',
  status: 'text-gray-400',
  blank: '',
  ready: 'text-green-400',
};

// ── Line Style Colors ────────────────────────────────────

const LINE_COLORS: Record<AdventureLine['style'], string> = {
  header: 'text-gray-300 font-semibold',
  data: 'text-cyan-300',
  narration: 'text-gray-400',
  success: 'text-green-400',
  warning: 'text-yellow-400',
  dim: 'text-gray-600',
  accent: 'text-amber-400',
  blank: '',
  hub: 'text-purple-400',
  ascii: 'text-gray-600',
  error: 'text-red-400',
};

function renderLine(line: AdventureLine): ReactElement {
  if (line.style === 'blank') return <>{'\u00A0'}</>;
  return <>{line.text}</>;
}

// ── Path Selector ────────────────────────────────────────

const PathSelector = ({
  onSelect,
  visitedPaths,
}: {
  onSelect: (pathId: string, firstSceneId: string) => void;
  visitedPaths: Set<string>;
}) => {
  const pathIcons = ['01', '02', '03', '04'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="mt-4"
    >
      <div className="text-gray-600 text-[11px] font-mono mb-3">
        choose your path:
      </div>
      {ALL_PATHS.map((path, idx) => {
        const visited = visitedPaths.has(path.id);
        return (
          <button
            key={path.id}
            onClick={() => onSelect(path.id, path.scenes[0].id)}
            className="group block w-full text-left py-2 transition-all duration-150 cursor-pointer"
          >
            <div className="flex items-start gap-3">
              <span className="text-gray-700 group-hover:text-amber-600 transition-colors font-mono text-[11px] mt-0.5">
                [{pathIcons[idx]}]
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`
                    font-mono group-hover:underline underline-offset-2 decoration-amber-800 transition-colors
                    ${visited ? 'text-gray-500' : 'text-amber-400/90 group-hover:text-amber-300'}
                  `}>
                    {path.title}
                  </span>
                  {visited && (
                    <span className="text-[9px] text-green-600 font-mono">VISITED</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-600 group-hover:text-gray-500 transition-colors">
                  {path.subtitle}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </motion.div>
  );
};

// ── Scene View ───────────────────────────────────────────

const SceneView = ({
  scene,
  isLatest,
  onChoice,
  sceneIndex,
}: {
  scene: AdventureScene;
  isLatest: boolean;
  onChoice: (next: string) => void;
  sceneIndex: number;
}) => {
  const [hubComplete, setHubComplete] = useState(!scene.hubEvent);

  return (
    <div className="mt-4">
      {/* Scene title */}
      <motion.div
        initial={isLatest ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex items-center gap-2 mb-2"
      >
        <span className="text-[10px] text-gray-700 font-mono">
          scene {sceneIndex + 1}
        </span>
        <span className="text-gray-800">|</span>
        <span className="text-[11px] text-gray-500 font-mono">
          {scene.title}
        </span>
      </motion.div>

      {/* Lines */}
      {scene.lines.map((line, i) => (
        <motion.div
          key={`${scene.id}-line-${i}`}
          initial={isLatest ? { opacity: 0, x: -4 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={isLatest ? { delay: i * 0.04, duration: 0.2 } : undefined}
          className={`${LINE_COLORS[line.style]} whitespace-pre-wrap text-[12px] md:text-[13px] leading-[1.7]`}
        >
          {renderLine(line)}
        </motion.div>
      ))}

      {/* Hub Visualization */}
      {isLatest && scene.hubEvent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: scene.lines.length * 0.04 + 0.3 }}
        >
          <HubVisualization
            event={scene.hubEvent}
            onComplete={() => setHubComplete(true)}
          />
        </motion.div>
      )}

      {/* Choices */}
      {isLatest && hubComplete && scene.choices.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: scene.hubEvent ? 0.5 : scene.lines.length * 0.04 + 0.3 }}
          className="mt-3"
        >
          <div className="text-gray-700 text-[11px] font-mono mb-1">
            your move:
          </div>
          {scene.choices.map((choice) => (
            <button
              key={choice.next}
              onClick={() => onChoice(choice.next)}
              className="group block w-full text-left py-1 text-gray-400 hover:text-amber-400 transition-all duration-150 cursor-pointer"
            >
              <span className="text-gray-700 group-hover:text-amber-600 transition-colors">
                {'  > '}
              </span>
              <span className="group-hover:underline underline-offset-2 decoration-amber-800">
                {choice.label}
              </span>
              {choice.tag && (
                <span className="text-[10px] text-gray-700 ml-2 group-hover:text-gray-500">
                  [{choice.tag}]
                </span>
              )}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// ── Progress Bar ─────────────────────────────────────────

const ProgressBar = ({
  visitedPaths,
  visitedScenes,
  totalScenes,
}: {
  visitedPaths: Set<string>;
  visitedScenes: Set<string>;
  totalScenes: number;
}) => {
  const progress = Math.round((visitedScenes.size / totalScenes) * 100);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#080808]/90 border-t border-gray-800/40 backdrop-blur-sm">
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-4">
        {/* Progress bar */}
        <div className="flex-1 h-1 bg-gray-800/60 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-amber-500/60 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-[10px] font-mono text-gray-600">
          <span>{visitedPaths.size}/4 paths</span>
          <span>{visitedScenes.size}/{totalScenes} scenes</span>
          <span>{progress}%</span>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────

export const CortexAdventure = () => {
  const termRef = useRef<HTMLDivElement>(null);

  // Boot sequence
  const [bootPhase, setBootPhase] = useState<'booting' | 'ready'>('booting');
  const [visibleBootLines, setVisibleBootLines] = useState(0);

  // Navigation
  const [mode, setMode] = useState<'paths' | 'scene'>('paths');
  const [currentPathId, setCurrentPathId] = useState<string | null>(null);
  const [sceneHistory, setSceneHistory] = useState<string[]>([]);

  // Tracking
  const [visitedPaths, setVisitedPaths] = useState<Set<string>>(new Set());
  const [visitedScenes, setVisitedScenes] = useState<Set<string>>(new Set());

  const totalScenes = ALL_PATHS.reduce((sum, p) => sum + p.scenes.length, 0);

  // Auto-scroll
  useEffect(() => {
    if (termRef.current) {
      termRef.current.scrollTop = termRef.current.scrollHeight;
    }
  }, [visibleBootLines, sceneHistory, mode]);

  // Boot sequence timer
  useEffect(() => {
    const timers: number[] = [];
    for (let i = 0; i < BOOT_LINES.length; i++) {
      timers.push(
        window.setTimeout(() => {
          setVisibleBootLines(i + 1);
          if (i === BOOT_LINES.length - 1) {
            window.setTimeout(() => setBootPhase('ready'), 500);
          }
        }, BOOT_LINES[i].delayMs)
      );
    }
    return () => timers.forEach(clearTimeout);
  }, []);

  // Keyboard navigation (1-4 for paths)
  useEffect(() => {
    if (mode !== 'paths' || bootPhase !== 'ready') return;
    const handler = (e: KeyboardEvent) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 4) {
        const path = ALL_PATHS[num - 1];
        handleSelectPath(path.id, path.scenes[0].id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, bootPhase]);

  const handleSelectPath = useCallback((pathId: string, firstSceneId: string) => {
    setCurrentPathId(pathId);
    setMode('scene');
    setSceneHistory([firstSceneId]);
    setVisitedPaths((prev) => new Set([...prev, pathId]));
    setVisitedScenes((prev) => new Set([...prev, firstSceneId]));
  }, []);

  const handleChoice = useCallback((next: string) => {
    if (next === 'hub') {
      setMode('paths');
      setSceneHistory([]);
      setCurrentPathId(null);
      return;
    }

    // If the choice leads to a scene in a different path, update currentPathId
    const targetPath = ALL_PATHS.find((p) =>
      p.scenes.some((s) => s.id === next)
    );
    if (targetPath && targetPath.id !== currentPathId) {
      setCurrentPathId(targetPath.id);
      setVisitedPaths((prev) => new Set([...prev, targetPath.id]));
    }

    setSceneHistory((prev) => [...prev, next]);
    setVisitedScenes((prev) => new Set([...prev, next]));
  }, [currentPathId]);

  const handleBackToPaths = useCallback(() => {
    setMode('paths');
    setSceneHistory([]);
    setCurrentPathId(null);
  }, []);

  const handleRestart = useCallback(() => {
    setMode('paths');
    setSceneHistory([]);
    setCurrentPathId(null);
    setVisitedPaths(new Set());
    setVisitedScenes(new Set());
  }, []);

  // ── Cursor ──
  const [showCursor, setShowCursor] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setShowCursor((v) => !v), 530);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 p-4 md:p-6">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Link
            to="/"
            className="text-sm font-mono text-gray-500 hover:text-gray-300 transition-colors"
          >
            &larr; Kempion
          </Link>
          <div className="flex items-center gap-2">
            <motion.div
              className="w-2 h-2 rounded-full bg-amber-500"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-xs font-mono text-amber-500/60 uppercase tracking-widest">
              Cortex
            </span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="min-h-screen flex flex-col items-center justify-center px-4 py-20 pb-16">
        <div className="w-full max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="rounded-xl border border-gray-800/80 overflow-hidden shadow-2xl shadow-amber-900/10"
          >
            {/* Title Bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-900/80 border-b border-gray-800/50">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500/60" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <span className="w-3 h-3 rounded-full bg-green-500/60" />
              </div>
              <span className="text-[11px] text-gray-600 font-mono ml-2">
                cortex -- interactive demo
              </span>
              <div className="ml-auto flex items-center gap-3">
                {mode === 'scene' && (
                  <button
                    onClick={handleBackToPaths}
                    className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono"
                  >
                    all paths
                  </button>
                )}
                {visitedScenes.size > 0 && (
                  <button
                    onClick={handleRestart}
                    className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono"
                  >
                    restart
                  </button>
                )}
              </div>
            </div>

            {/* Terminal Body */}
            <div
              ref={termRef}
              className="bg-[#080808] px-5 py-5 md:px-6 md:py-6 font-mono text-[12px] md:text-[13px] leading-[1.7] min-h-[480px] max-h-[700px] overflow-y-auto overflow-x-auto"
              role="log"
              aria-label="Cortex interactive demo terminal"
            >
              {/* Boot sequence */}
              {BOOT_LINES.slice(0, visibleBootLines).map((line, i) => (
                <div key={i} className={`${BOOT_COLORS[line.style]} whitespace-pre`}>
                  {line.style === 'blank' ? '\u00A0' : line.text}
                  {i === visibleBootLines - 1 && bootPhase === 'booting' && (
                    <span
                      className={`inline-block w-[0.55em] h-[1.15em] bg-amber-400/80 align-text-bottom ml-px ${
                        showCursor ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{ transition: 'opacity 0.1s' }}
                    />
                  )}
                </div>
              ))}

              {/* Post-boot content */}
              {bootPhase === 'ready' && (
                <>
                  {/* Breadcrumbs */}
                  {mode === 'scene' && currentPathId && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-1 mt-3 mb-1 text-[10px] text-gray-700"
                    >
                      <button
                        onClick={handleBackToPaths}
                        className="hover:text-gray-500 transition-colors"
                      >
                        paths
                      </button>
                      <span>/</span>
                      <span className="text-gray-500">
                        {ALL_PATHS.find((p) => p.id === currentPathId)?.title}
                      </span>
                    </motion.div>
                  )}

                  {/* Path selector */}
                  {mode === 'paths' && (
                    <>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-3"
                      >
                        <div className="text-gray-600 whitespace-pre">
                          {'-- CORTEX INTELLIGENCE DEMO ----------'}
                        </div>
                        <div className="text-gray-400 mt-2">
                          Four paths through the intelligence layer.
                        </div>
                        <div className="text-gray-500 text-[11px]">
                          Each takes 2-3 minutes. Explore any order.
                        </div>
                      </motion.div>
                      <PathSelector
                        onSelect={handleSelectPath}
                        visitedPaths={visitedPaths}
                      />
                    </>
                  )}

                  {/* Scene renderer */}
                  {mode === 'scene' && (
                    <AnimatePresence>
                      {sceneHistory.map((sceneId, idx) => {
                        const scene = SCENE_MAP[sceneId];
                        if (!scene) return null;

                        // Calculate scene index within its path
                        const scenePath = ALL_PATHS.find((p) =>
                          p.scenes.some((s) => s.id === sceneId)
                        );
                        const sceneIndex = scenePath
                          ? scenePath.scenes.findIndex((s) => s.id === sceneId)
                          : 0;

                        return (
                          <SceneView
                            key={`${sceneId}-${idx}`}
                            scene={scene}
                            isLatest={idx === sceneHistory.length - 1}
                            onChoice={handleChoice}
                            sceneIndex={sceneIndex}
                          />
                        );
                      })}
                    </AnimatePresence>
                  )}

                  {/* Completion message */}
                  {visitedPaths.size === 4 && mode === 'paths' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="mt-6 p-4 rounded-lg border border-amber-500/20 bg-amber-500/5"
                    >
                      <div className="text-amber-400 text-[12px] font-semibold">
                        All paths explored.
                      </div>
                      <div className="text-gray-400 text-[11px] mt-1">
                        You have seen the full intelligence layer: memory that compounds,
                        routing that saves 83%, portfolio awareness across 6 projects,
                        and the vision for what comes next.
                      </div>
                      <div className="text-gray-500 text-[11px] mt-2">
                        This is not a demo. This is the production system.
                      </div>
                    </motion.div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      {/* Progress bar */}
      {bootPhase === 'ready' && (
        <ProgressBar
          visitedPaths={visitedPaths}
          visitedScenes={visitedScenes}
          totalScenes={totalScenes}
        />
      )}

      {/* Background glow -- amber tint for Cortex theme */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px] opacity-30" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-500/5 rounded-full blur-[120px] opacity-20" />
      </div>
    </div>
  );
};
